/* ── Loading / Skeleton ── */
.loading {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0;
}

.loadingSkeleton {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  height: 72px;
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.9;
  }
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--text-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.miniSpinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--border-color);
  border-top-color: var(--text-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Empty & Error states ── */
.emptyState {
  text-align: center;
  padding: 4rem 2rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  box-shadow: var(--card-shadow);
}

.emptyIcon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.emptyState h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  color: var(--text-primary);
  font-family: var(--font-heading);
}

.emptyState p {
  color: var(--text-secondary);
  margin: 0 0 1.5rem;
  line-height: 1.6;
}

.useCaseGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.625rem;
  max-width: 520px;
  margin: 0 auto;
  text-align: left;
}

.useCaseCard {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.875rem 1rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  font-size: 0.78rem;
  color: var(--text-secondary);
  line-height: 1.45;
  transition:
    border-color 0.2s,
    background 0.2s,
    transform 0.15s;
  cursor: pointer;
  text-decoration: none;
}
.useCaseCard:hover {
  border-color: var(--text-accent);
  background: rgba(34, 211, 238, 0.04);
  transform: translateY(-1px);
}
.aiChatLink {
  color: var(--text-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.useCaseCard:hover {
  border-color: rgba(34, 211, 238, 0.2);
  background: rgba(34, 211, 238, 0.03);
}

.useCaseIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    rgba(34, 211, 238, 0.1),
    rgba(168, 85, 247, 0.06)
  );
  color: var(--text-accent);
  margin-bottom: 0.25rem;
  flex-shrink: 0;
}

.useCaseCard strong {
  color: var(--text-primary);
  font-size: 0.84rem;
  font-weight: 600;
}

@media (max-width: 480px) {
  .useCaseGrid {
    grid-template-columns: 1fr;
  }
}

.link {
  color: var(--text-accent);
  text-decoration: underline;
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  background: var(--color-error-light);
  border: 1px solid var(--color-error-border);
  border-radius: 12px;
  color: var(--color-error);
}

.warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  background: var(--color-warning-light);
  border: 1px solid var(--color-warning-border);
  border-radius: 12px;
  color: var(--color-warning);
}

.retryBtn {
  background: transparent;
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
}

.retryBtn:hover {
  background: var(--color-error-light);
}

.errorBanner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--color-error-light);
  border: 1px solid var(--color-error-border);
  border-radius: 8px;
  color: var(--color-error);
  font-size: 0.875rem;
}

/* ── Watch list layout ── */
.watchesList {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.listHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text-secondary);
  font-size: 0.875rem;
  min-height: 36px;
}

.refreshBtn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 90px;
  justify-content: center;
}

.refreshBtn:hover {
  background: var(--btn-hover-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.refreshBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refreshIcon {
  display: inline-block;
  transition: transform 0.2s;
}

.refreshBtnSpinning .refreshIcon {
  animation: spin 1s linear infinite;
}

.refreshBtnIconOnly {
  min-width: unset;
  padding: 0.4rem;
  font-size: 1rem;
}

/* ── Add watch form ── */
.addWatchForm {
  /* A block box with no gap: every child sat flush against the next one, so the
     URL row, the suggestion chips and the page preview ran together with nothing
     between them. It holds a whole picker now, not just one input row. */
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.25rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 14px;
  padding: 1.1rem;
  box-shadow: var(--card-shadow);
  transition:
    border-color 0.3s,
    box-shadow 0.3s;
}

.formLoading {
  border-color: rgba(34, 211, 238, 0.5);
  animation: borderPulse 1.5s ease-in-out infinite;
}

.formError {
  border-color: var(--color-error);
  box-shadow:
    var(--card-shadow),
    0 0 8px rgba(239, 68, 68, 0.15);
}

@keyframes borderPulse {
  0%,
  100% {
    border-color: rgba(34, 211, 238, 0.3);
    box-shadow:
      var(--card-shadow),
      0 0 4px rgba(34, 211, 238, 0.05);
  }
  50% {
    border-color: rgba(34, 211, 238, 0.7);
    box-shadow:
      var(--card-shadow),
      0 0 14px rgba(34, 211, 238, 0.2);
  }
}

.addWatchRow {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.urlInputGroup {
  flex: 1;
  display: flex;
  align-items: center;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.urlInputGroup:focus-within {
  border-color: rgba(34, 211, 238, 0.4);
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.08);
}

.protocolSelect {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.4rem center;
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-family: var(--font-mono, "SF Mono", "Fira Code", monospace);
  padding: 0.65rem 1.4rem 0.65rem 0.75rem;
  cursor: pointer;
  outline: none;
  border-right: 1px solid var(--border-color);
  border-radius: 0;
  flex-shrink: 0;
  transition: color 0.15s;
}

.protocolSelect:hover {
  color: var(--text-primary);
}

.protocolSelect:focus {
  outline: none;
}

.addWatchInput {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: var(--font-body);
  padding: 0.65rem 1rem;
  outline: none;
  min-width: 0;
}

.addWatchInput::placeholder {
  color: var(--text-secondary);
  opacity: 0.5;
}

.addWatchBtn {
  background: linear-gradient(135deg, #0891b2, #22d3ee);
  color: #fff;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
  font-family: var(--font-body);
}

.addWatchBtn:hover:not(:disabled) {
  opacity: 0.9;
}

.addWatchBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.intentRow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.625rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.intentRow:focus-within {
  border-color: rgba(168, 85, 247, 0.4);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.08);
}

.intentIcon {
  flex-shrink: 0;
  color: var(--text-secondary);
  opacity: 0.5;
}

.intentInput {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.8rem;
  font-family: var(--font-body);
  padding: 0.15rem 0;
  outline: none;
  min-width: 0;
}

.intentInput::placeholder {
  color: var(--text-secondary);
  opacity: 0.5;
}

.addWatchError {
  color: var(--color-error);
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

.addWatchSuccess {
  margin-top: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: var(--color-success-light, rgba(16, 185, 129, 0.08));
  border: 1px solid var(--color-success-border, rgba(16, 185, 129, 0.25));
  border-radius: 8px;
  font-size: 0.825rem;
  color: var(--color-success, #059669);
}

.upgradeBanner {
  margin-top: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: var(--color-warning-light);
  border: 1px solid var(--color-warning-border);
  border-radius: 8px;
  font-size: 0.825rem;
  color: var(--color-warning);
}

.upgradeLink {
  color: var(--text-accent);
  font-weight: 500;
  text-decoration: underline;
}

/* ── Plan banners ── */
.planBannerWarning,
.planBannerError {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.825rem;
  margin-bottom: 0.5rem;
}

.planBannerWarning {
  background: var(--color-warning-light);
  border: 1px solid var(--color-warning-border);
  color: var(--color-warning);
}

.planBannerError {
  background: var(--color-error-light, rgba(239, 68, 68, 0.08));
  border: 1px solid var(--color-error-border, rgba(239, 68, 68, 0.2));
  color: var(--color-error, #ef4444);
}

.planBannerLink {
  white-space: nowrap;
  font-weight: 500;
  text-decoration: none;
  color: var(--text-accent);
}

.planBannerLink:hover {
  text-decoration: underline;
}

.planBannerDismiss {
  background: none;
  border: none;
  color: inherit;
  opacity: 0.5;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.25rem;
  margin-left: auto;
  flex-shrink: 0;
}

.planBannerDismiss:hover {
  opacity: 1;
}

/* ── Upgrade nudge (free users with changes) ── */
.upgradeBanner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.825rem;
  margin-bottom: 0.5rem;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: var(--text-primary);
}

.upgradeBannerLink {
  font-weight: 500;
  text-decoration: none;
  color: #d97706;
}

.upgradeBannerLink:hover {
  text-decoration: underline;
}

/* ── Trial notification setup nudge ── */
.trialNudgeBanner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.825rem;
  margin-bottom: 0.5rem;
  background: rgba(168, 85, 247, 0.06);
  border: 1px solid rgba(168, 85, 247, 0.15);
  color: var(--text-primary);
}

.trialNudgeLink {
  white-space: nowrap;
  font-weight: 500;
  text-decoration: none;
  color: #a855f7;
}

.trialNudgeLink:hover {
  text-decoration: underline;
}

/* ── Review prompt ── */
.reviewBanner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  background: rgba(34, 211, 238, 0.06);
  border: 1px solid rgba(34, 211, 238, 0.15);
  border-radius: 8px;
  font-size: 0.825rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}

.reviewBtn {
  padding: 0.3rem 0.65rem;
  background: rgba(34, 211, 238, 0.12);
  color: var(--text-accent);
  border-radius: 6px;
  font-size: 0.775rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.reviewBtn:hover {
  background: rgba(34, 211, 238, 0.2);
}

.reviewDismiss {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1rem;
  padding: 0.2rem;
  line-height: 1;
  opacity: 0.5;
}

.reviewDismiss:hover {
  opacity: 1;
}

@media (max-width: 600px) {
  .planBannerWarning,
  .planBannerError {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
  }

  .modalOverlay {
    padding: 1rem 0;
  }

  /* Watch settings — mobile adjustments */
  .settingsHeader {
    padding: 0.875rem 1rem;
  }

  .settingsBody {
    padding: 1rem;
  }

  /* Mode rows: hide icon on mobile, more space for text */
  .modeRow {
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .processorIcon {
    display: none;
  }

  .processorTitle {
    font-size: 0.82rem;
  }

  .processorDesc {
    font-size: 0.72rem;
  }

  .alwaysOn {
    font-size: 0.65rem;
  }

  /* Interval card: stack on mobile */
  .intervalCard {
    flex-direction: column;
    align-items: flex-start;
    padding: 0.75rem;
    gap: 0.5rem;
  }

  .intervalControls {
    width: 100%;
    margin-left: 0;
    margin-top: 0;
    display: flex;
    gap: 0.4rem;
  }

  .intervalControls .intervalInput {
    flex: 1;
    min-width: 0;
    width: auto;
  }

  .intervalControls .intervalSelect {
    flex: 1;
    min-width: 0;
  }

  .intervalSection {
    margin-top: 1rem;
  }

  .ignoreSection {
    margin-top: 1rem;
  }

  .ignoreCard {
    padding: 0.75rem;
  }

  .ignoreCardHeader {
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .ignoreSaveBtn {
    padding: 0.65rem 1rem;
    font-size: 0.82rem;
  }
}

/* ── Unified filter toolbar ── */
.filterToolbar {
  display: flex;
  align-items: center;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 0.4rem 0.625rem;
  box-shadow: var(--card-shadow);
  transition: border-color 0.2s;
}

.filterToolbar:focus-within {
  border-color: rgba(34, 211, 238, 0.2);
}

.toolbarSearch {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.toolbarSearchIcon {
  width: 15px;
  height: 15px;
  color: var(--text-secondary);
  flex-shrink: 0;
  opacity: 0.6;
}

.toolbarSearchInput {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 0.85rem;
  font-family: var(--font-body);
  outline: none;
  min-width: 0;
  padding: 0.25rem 0;
}

.toolbarSearchInput::placeholder {
  color: var(--text-secondary);
  opacity: 0.5;
}

.toolbarSearchClear {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0 0.15rem;
  line-height: 1;
  opacity: 0.4;
  transition: opacity 0.15s;
}

.toolbarSearchClear:hover {
  opacity: 1;
  color: var(--text-primary);
}

.toolbarDivider {
  width: 1px;
  height: 18px;
  background: var(--border-color);
  flex-shrink: 0;
  margin: 0 0.375rem;
  opacity: 0.5;
}

.toolbarFilters {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
}

.filterSelect {
  appearance: none;
  -webkit-appearance: none;
  padding: 0.25rem 1.4rem 0.25rem 0.5rem;
  border: 1px solid transparent;
  border-radius: 6px;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.35rem center;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  height: 28px;
}

.filterSelect:hover {
  background-color: var(--btn-hover-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.filterSelect:focus {
  outline: none;
  border-color: rgba(34, 211, 238, 0.3);
}

.filterSelectActive {
  color: var(--text-accent);
  background-color: rgba(34, 211, 238, 0.06);
  border-color: rgba(34, 211, 238, 0.15);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2322d3ee' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

.clearFiltersBtn {
  display: inline-flex;
  align-items: center;
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  white-space: nowrap;
  height: 28px;
  transition: all 0.15s;
  font-family: var(--font-body);
  opacity: 0.6;
}

.clearFiltersBtn:hover {
  opacity: 1;
  background: var(--btn-hover-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

/* ── Active filter pills ── */
.activeFilters {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  padding: 0 0.25rem;
}

.activeFiltersLabel {
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  opacity: 0.5;
  margin-right: 0.1rem;
}

.filterPill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.4rem;
  border-radius: 5px;
  background: rgba(34, 211, 238, 0.06);
  border: 1px solid rgba(34, 211, 238, 0.12);
  color: var(--text-accent);
  font-size: 0.68rem;
  font-weight: 500;
  white-space: nowrap;
  font-family: var(--font-body);
  transition: all 0.15s;
}

.filterPill:hover {
  background: rgba(34, 211, 238, 0.1);
  border-color: rgba(34, 211, 238, 0.25);
}

.filterPillDismiss {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  line-height: 1;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.filterPillDismiss:hover {
  opacity: 1;
}

/* ── Tag filter dropdown ── */
.tagFilterWrapper {
  position: relative;
}

.filterBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}

.filterBtn:hover {
  background: var(--btn-hover-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.filterActive {
  border-color: rgba(34, 211, 238, 0.15);
  color: var(--text-accent);
  background: rgba(34, 211, 238, 0.06);
}

.filterIcon {
  width: 14px;
  height: 14px;
}

.tagDropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.375rem;
  z-index: 50;
  min-width: 170px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.tagOption {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  background: none;
  border: none;
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 0.8rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  font-family: var(--font-body);
}

.tagOption:hover {
  background: var(--btn-hover-bg);
}

.tagOptionActive {
  background: var(--btn-hover-bg);
}

.tagCircle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tagCheckmark {
  margin-left: auto;
  color: var(--text-accent);
  font-size: 0.75rem;
}

/* ── Tag selector on watch rows ── */
.tagSelectorWrapper {
  position: relative;
}

.currentTags {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  min-width: 28px;
  min-height: 28px;
  transition: background 0.15s;
}

.currentTags:hover {
  background: var(--btn-hover-bg);
}

.miniTagCircle {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.noTags {
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1;
}

.tagSpinner {
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1.5px solid var(--border-color);
  border-top-color: var(--text-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

.tagSelectorDropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.5rem;
  z-index: 50;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  max-width: 200px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.tagOptionCircle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition:
    border-color 0.15s,
    opacity 0.15s;
}

.tagOptionCircle:hover {
  border-color: var(--border-color);
}

.tagOptionCircle:disabled {
  opacity: 0.5;
  cursor: wait;
}

.tagOptionCircleActive {
  border-color: var(--text-accent);
}

.tagCircleCheck {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 0.55rem;
  font-weight: 700;
  pointer-events: none;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.tagFilterGrid {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.5rem;
  z-index: 50;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 200px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.tagFilterCircleBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: border-color 0.15s;
}

.tagFilterCircleBtn:hover {
  border-color: var(--border-color);
}

.tagFilterCircleBtnActive {
  border-color: var(--text-accent);
}

.tagFilterCircle {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ═══════════════════════════════════════════
   Table layout — consistent column alignment
   ═══════════════════════════════════════════ */

.tableWrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.tableHeader,
.tableRow {
  display: grid;
  /* One source of truth for the actions width, so the header and the rows
     cannot drift apart. Keep this in step with .actionBtn width and the
     .watchActions gap. */
  --actions-col: 480px;
  /* The actions track MUST be an explicit width, never max-content: the header
     and the rows are separate grid containers, so max-content resolves to ~0
     in the header (its actions cell is empty) and ~480px in the rows, which
     slides every other column out from under its own heading.
     11 buttons x 38px + 10 gaps x 6px = 478. */
  /* The status track carries the health card, not just a badge — at 105px the
     card overflowed into Checked and Changed and printed on top of them. */
  grid-template-columns: minmax(0, 1fr) 200px 90px 90px var(--actions-col);
  gap: 0.75rem;
  align-items: center;
}

.tableHeader {
  padding: 0 1.25rem 0.375rem;
}

.th {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  white-space: nowrap;
  user-select: none;
}

.sortableHeader {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  user-select: none;
}

.sortableHeader:hover {
  color: var(--text-primary);
}

.sortIndicator {
  font-size: 0.55rem;
  color: var(--text-accent);
}

.tableBody {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tableRow {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 14px;
  padding: 0.75rem 1.25rem;
  transition: all 0.2s;
  box-shadow: var(--card-shadow);
}

.tableRow:hover {
  background: var(--btn-hover-bg);
  border-color: var(--border-color);
  box-shadow: var(--card-shadow-hover);
}

.tableRow.hasError {
  border-color: var(--color-error-border);
}

.td {
  min-width: 0;
}

/* ── Site cell (favicon + title + url) ── */
.siteCell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.siteInfo {
  min-width: 0;
  flex: 1;
}

.favicon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
}

.watchTitle {
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  font-size: 0.9rem;
  line-height: 1.3;
}

.editTitleInput {
  font-weight: 500;
  color: var(--text-primary);
  background: transparent;
  border: 1px solid var(--text-accent);
  border-radius: 4px;
  padding: 0.15rem 0.4rem;
  font-size: inherit;
  font-family: inherit;
  width: 100%;
  outline: none;
}

.watchUrl {
  display: block;
  font-size: 0.75rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  text-decoration: none;
  width: fit-content;
  max-width: 100%;
}

.watchUrl:hover,
.watchUrl:focus-visible {
  color: var(--text-accent);
  text-decoration: underline;
}

/* ── Time values ── */
.timeValue {
  font-size: 0.8rem;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.checking {
  color: var(--text-accent);
  font-size: 0.8rem;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* ── Status badges ── */
.badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 9999px;
  white-space: nowrap;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  position: relative;
  cursor: default;
}

.badge::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 0.72rem;
  font-weight: 400;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  min-width: 180px;
  max-width: 320px;
  white-space: normal;
  text-align: center;
  text-transform: none;
  letter-spacing: normal;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 20;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

/* Gated, because there is nothing to hover with on a phone and a rule that only
   works for a mouse should say so. The badge carries tabindex="0" so the same
   text is reachable by focus - which also gives it to keyboard users, who could
   not read it either. */
@media (hover: hover) {
  .badge:hover::after {
    opacity: 1;
  }
}

.badge:focus-visible::after {
  opacity: 1;
}

.badge:focus-visible {
  outline: 2px solid var(--text-accent);
  outline-offset: 2px;
  border-radius: 9999px;
}

.badgeChanged {
  background: rgba(34, 197, 94, 0.12);
  color: #22c55e;
}

.badgeViewed {
  background: rgba(96, 165, 250, 0.12);
  color: #60a5fa;
}

.badgeNoChange {
  background: rgba(148, 163, 184, 0.12);
  color: var(--text-secondary);
}

.badgePending {
  background: rgba(251, 191, 36, 0.12);
  color: #f59e0b;
}

.badgeError {
  background: var(--color-error-light);
  color: var(--color-error);
}

.reliabilityCard {
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border: 1px solid
    color-mix(in srgb, var(--color-error, #dc2626) 22%, transparent);
  border-radius: 10px;
  background: color-mix(in srgb, var(--color-error, #dc2626) 5%, transparent);
  padding: 8px 10px;
}

.reliabilityMessage {
  color: var(--text-primary);
  font-size: 12px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.reliabilityProgress {
  color: var(--text-secondary);
  font-size: 11px;
  line-height: 1.3;
}

/* Deliberately inert — no pulse, no alarm colour. A paused watch is off, and
   should not compete for attention with one that is actually failing. */
.badgePaused {
  background: rgba(148, 163, 184, 0.14);
  color: var(--text-secondary, #94a3b8);
}

.badgeChecking {
  background: rgba(34, 211, 238, 0.12);
  color: var(--text-accent);
  animation: pulse 1.5s ease-in-out infinite;
}

/* Tuning is AI work on the watch's filters, not a fetch of the page, so it gets
   its own hue rather than reading as another check. */
.badgeTuning {
  background: rgba(167, 139, 250, 0.14);
  color: #a78bfa;
  animation: pulse 1.5s ease-in-out infinite;
}

/* Marks the auto-tune action as worth using on a watch the noise heuristic
   flagged, without hiding it on every other watch. */
.actionBtnSuggested {
  border-color: color-mix(in srgb, #a78bfa 45%, transparent);
  color: #a78bfa;
}

.badgeFilterBroken {
  background: rgba(249, 115, 22, 0.14);
  color: #ea580c;
}

.badgeFetchSuspicious {
  background: rgba(168, 85, 247, 0.14);
  color: #9333ea;
}

/* ── Action buttons ── */
.watchActions {
  display: flex;
  gap: 0.375rem;
  /* One row, all equal width. The space this needs came from dropping the
     tags column; without that the buttons had to either squeeze unevenly or
     spill outside the card. */
  flex-wrap: nowrap;
}

.actionBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Wider than tall: these sit in a dense row and 32px square left the glyphs
     crowding their own borders, which read as one smudged block rather than
     distinct controls. */
  width: 38px;
  height: 32px;
  /* Flex children shrink below their width by default, so in a narrow column
     these squeezed to whatever fitted — unevenly, because the recheck button
     sits one level deeper inside .recheckWrap and so shrank twice. Pinning
     both keeps every control the same size. */
  flex-shrink: 0;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  padding: 0;
  line-height: 1;
  position: relative;
}

.actionBtn::after {
  content: attr(title);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 0.675rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

@media (hover: hover) {
  .actionBtn:hover::after {
    opacity: 1;
  }
}

.actionBtnDisabled::after {
  display: none;
}

.actionIcon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.actionBtnDisabled {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}

.actionBtnSuccess {
  color: #22c55e;
  border-color: rgba(34, 197, 94, 0.3);
  background: rgba(34, 197, 94, 0.08);
}

.actionBtn[title="View diff"]:hover,
.actionBtn[title*="diff" i]:hover {
  color: var(--text-accent);
  border-color: rgba(34, 211, 238, 0.3);
  background: rgba(34, 211, 238, 0.06);
}

.actionBtn[title="View timeline"]:hover,
.actionBtn[title*="timeline" i]:hover {
  color: var(--accent-warm);
  border-color: rgba(251, 146, 60, 0.3);
  background: rgba(251, 146, 60, 0.06);
}

.actionBtn[title="Open website"]:hover {
  color: var(--text-primary);
  border-color: var(--border-color);
  background: var(--btn-hover-bg);
}

.actionBtn:hover {
  background: var(--btn-hover-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.actionBtn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.deleteBtn:hover:not(:disabled) {
  color: var(--color-error);
  border-color: var(--color-error-border);
  background: var(--color-error-light);
}

.recheckBtnSpinning {
  opacity: 0.6;
  cursor: not-allowed;
}

.recheckBtnSpinning .actionIcon {
  animation: spin 1s linear infinite;
}

.actionBtn[title="Recheck now"]:hover:not(:disabled) {
  color: var(--text-accent);
  border-color: rgba(34, 211, 238, 0.3);
  background: rgba(34, 211, 238, 0.06);
}

.recheckWrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.recheckCounter {
  position: absolute;
  bottom: -2px;
  right: -2px;
  font-size: 8px;
  font-weight: 600;
  font-family: var(--font-mono, monospace);
  color: var(--text-muted);
  background: var(--bg-secondary, #1e293b);
  border-radius: 4px;
  padding: 0 3px;
  line-height: 1.4;
  pointer-events: none;
}

.recheckExhausted {
  color: var(--text-muted);
  opacity: 0.5;
}

.recheckBtnExhausted {
  opacity: 0.35;
}

/* ── Camera badge on favicon ── */
.faviconWrap {
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.cameraBadge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  color: #7c3aed;
  filter: drop-shadow(0 0 1px var(--card-bg));
}

/* ── Screenshot toggle in action bar ── */
/* A paused watch is doing nothing, which is easy to miss in a long list —
   the resume control is the one action that changes that, so it reads as
   "go" rather than as one more grey glyph. */
.resumeBtn {
  color: #22c55e !important;
  background: rgba(34, 197, 94, 0.12) !important;
  border-color: rgba(34, 197, 94, 0.3) !important;
  border-radius: 8px;
}

.resumeBtn:hover {
  background: rgba(34, 197, 94, 0.2) !important;
}

.screenshotActive {
  color: #a855f7 !important;
  background: rgba(168, 85, 247, 0.12) !important;
  border-color: rgba(168, 85, 247, 0.3) !important;
  border-radius: 8px;
}

.screenshotActive:hover {
  background: rgba(168, 85, 247, 0.2) !important;
}

.screenshotActive::after {
  content: "Screenshots on" !important;
}

.screenshotInactive {
  opacity: 0.45;
}

.screenshotInactive:hover {
  opacity: 1;
  color: #a855f7 !important;
}

.screenshotInactive::after {
  content: "Enable screenshots" !important;
}

.screenshotLocked {
  opacity: 0.3;
}

.screenshotLocked:hover {
  opacity: 0.6;
}

.screenshotLocked::after {
  content: "Pro feature" !important;
}

/* ── Show more button ── */
.showMoreBtn {
  display: block;
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  border: 1px dashed var(--border-color);
  border-radius: 12px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: var(--font-body);
}

.showMoreBtn:hover {
  background: var(--btn-hover-bg);
  border-color: var(--text-accent);
  color: var(--text-primary);
}

/* ── Confirmation modal ── */
.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  backdrop-filter: blur(4px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 2rem 0;
}

.modal {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.modalTitle {
  font-size: 1.125rem;
  font-weight: 600;
  font-family: var(--font-heading);
  color: var(--text-primary);
  margin: 0 0 0.5rem;
}

.modalMessage {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modalActions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.modalCancelBtn {
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  font-family: var(--font-body);
}

.modalCancelBtn:hover:not(:disabled) {
  background: var(--btn-hover-bg);
  color: var(--text-primary);
}

.modalConfirmBtn {
  background: var(--color-error);
  border: none;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: opacity 0.2s;
  font-family: var(--font-body);
}

.modalConfirmBtn:hover:not(:disabled) {
  opacity: 0.9;
}

.modalConfirmBtn:disabled,
.modalCancelBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Watch settings — full-screen on mobile, centered dialog on desktop ── */
.settingsModal {
  position: fixed;
  inset: 0;
  background: var(--bg-secondary);
  z-index: 210;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: settingsSlideIn 0.2s ease-out;
}

@keyframes settingsSlideIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Desktop: backdrop + centered panel */
@media (min-width: 768px) {
  .settingsModal {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    align-items: center;
    justify-content: center;
    animation: settingsBackdropIn 0.2s ease-out;
  }

  @keyframes settingsBackdropIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}

.settingsPanel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

@media (min-width: 768px) {
  .settingsPanel {
    max-width: 560px;
    height: auto;
    max-height: calc(100vh - 80px);
    background: var(--bg-secondary);
    border-radius: 16px;
    border: 1px solid var(--border-color);
    box-shadow:
      0 25px 60px rgba(0, 0, 0, 0.35),
      0 0 0 1px rgba(255, 255, 255, 0.03);
    animation: settingsPanelIn 0.25s ease-out;
  }

  @keyframes settingsPanelIn {
    from {
      opacity: 0;
      transform: translateY(16px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
}

.settingsHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  background: var(--bg-secondary);
  position: sticky;
  top: 0;
  z-index: 1;
}

@media (min-width: 768px) {
  .settingsHeader {
    padding: 1.25rem 2rem;
    border-radius: 16px 16px 0 0;
  }
}

.settingsHeader h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font-heading);
  color: var(--text-primary);
}

.settingsClose {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  transition: color 0.2s;
}

.settingsClose:hover {
  color: var(--text-primary);
}

.settingsBody {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1.5rem;
  max-width: 520px;
  width: 100%;
  margin: 0 auto;
}

.settingsFieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.settingsFieldset:disabled {
  opacity: 0.6;
}

@media (min-width: 768px) {
  .settingsBody {
    max-width: 100%;
    padding: 1.75rem 2rem 2rem;
  }
}

.settingsLabel {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.monitoringModes {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modeRow {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-family: var(--font-body);
}

.modeRowLocked {
  opacity: 0.55;
}

.modeInfo {
  flex: 1;
  min-width: 0;
}

.alwaysOn {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  white-space: nowrap;
}

/* Toggle switch */
.toggleSwitch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
}

.toggleSwitch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggleSlider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: var(--border-color);
  border-radius: 22px;
  transition: background 0.25s;
}

.toggleSlider::before {
  content: "";
  position: absolute;
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.25s;
}

.toggleSwitch input:checked + .toggleSlider {
  background: var(--text-accent);
}

.toggleSwitch input:checked + .toggleSlider::before {
  transform: translateX(18px);
}

.toggleSwitch input:disabled + .toggleSlider {
  cursor: not-allowed;
  opacity: 0.5;
}

.processorIcon {
  width: 20px;
  height: 20px;
  color: var(--text-accent);
  flex-shrink: 0;
  margin-top: 2px;
}

.processorTitle {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.processorDesc {
  font-size: 0.78rem;
  color: var(--text-secondary);
  margin-top: 2px;
  line-height: 1.4;
}

.proBadge {
  display: inline-block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1px 6px;
  border-radius: 4px;
  background: linear-gradient(135deg, #7c3aed, #6366f1);
  color: #fff;
  font-weight: 700;
}

.upgradeLink {
  display: block;
  margin-top: 1rem;
  font-size: 0.82rem;
  color: var(--text-accent);
  text-decoration: none;
  text-align: center;
  transition: opacity 0.2s;
}

.upgradeLink:hover {
  opacity: 0.8;
  text-decoration: underline;
}

/* Check Interval section */
.intervalSection {
  margin-top: 1.5rem;
}

.intervalCard {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-family: var(--font-body);
}

.intervalControls {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
}

.intervalInput {
  width: 52px;
  padding: 0.4rem 0.5rem;
  border-radius: 7px;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.82rem;
  font-family: var(--font-body);
  text-align: center;
  transition: border-color 0.2s;
}

.intervalInput:focus {
  outline: none;
  border-color: var(--text-accent);
}

.intervalSelect {
  padding: 0.4rem 0.5rem;
  padding-right: 1.4rem;
  border-radius: 7px;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.82rem;
  font-family: var(--font-body);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  cursor: pointer;
  transition: border-color 0.2s;
}

.intervalSelect:focus {
  outline: none;
  border-color: var(--text-accent);
}

.intervalSaveBtn {
  padding: 0.4rem 0.75rem;
  border-radius: 7px;
  background: linear-gradient(135deg, #0891b2, #22d3ee);
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 500;
  font-family: var(--font-body);
  transition:
    opacity 0.15s,
    transform 0.15s;
  white-space: nowrap;
}

.intervalSaveBtn:hover:not(:disabled) {
  opacity: 0.88;
  transform: translateY(-1px);
}

.intervalSaveBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

/* ── Ignore Rules Section ── */

.ignoreSection {
  margin-top: 1.5rem;
}

.ignoreCard {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.5rem;
  transition: border-color 0.2s;
}

.ignoreCard:focus-within {
  border-color: var(--text-accent);
}

/* The re-tune card sits after the save button, which only carries a top margin,
   so without this the two boxes touch. */
.ignoreSaveBtn + .ignoreCard {
  margin-top: 0.85rem;
}

.ignoreCardHeader {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 0.65rem;
}

.ignoreTextarea {
  width: 100%;
  min-height: 56px;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: "SF Mono", "Fira Code", "Fira Mono", monospace;
  font-size: 0.78rem;
  line-height: 1.55;
  resize: vertical;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.ignoreTextarea::placeholder {
  color: var(--text-muted);
  opacity: 0.45;
}

.ignoreTextarea:focus {
  outline: none;
  border-color: var(--text-accent);
  box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.1);
}

.ignoreSaveBtn {
  width: 100%;
  margin-top: 0.65rem;
  padding: 0.6rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: var(--font-body);
  color: #fff;
  background: linear-gradient(135deg, #0891b2, #22d3ee);
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition:
    opacity 0.2s,
    transform 0.1s,
    border-color 0.2s;
}

.ignoreSaveBtn:hover {
  opacity: 0.88;
}

.ignoreSaveBtn:active {
  transform: scale(0.985);
}

/* ═══════════════════════════════════════════
   Responsive
   ═══════════════════════════════════════════ */

/* Narrow desktop: hide "Changed" column */
@media (max-width: 1100px) {
  .th[data-column="lastChanged"],
  .td[data-column="lastChanged"] {
    display: none;
  }

  .tableHeader,
  .tableRow {
    grid-template-columns: minmax(0, 1fr) 200px 90px var(--actions-col);
  }
}

/* Tablet / mobile: stack into cards */
@media (max-width: 960px) {
  .tableHeader {
    display: none;
  }

  /* Show Changed column again in card layout */
  .td[data-column="lastChanged"] {
    display: flex;
  }

  .tableRow {
    /* `auto 1fr`, not `1fr auto`. Only one row uses these two tracks - the pair of
       timestamps - and every other row spans both. With the flexible track first,
       "Last checked" and "Last changed" were shoved to opposite edges of the card
       with a finger's width of nothing between them, reading as two unrelated
       facts rather than one line about when this watch last did anything. */
    grid-template-columns: auto 1fr;
    grid-template-rows: auto;
    gap: 0.625rem;
    padding: 0.875rem 1rem;
  }

  /* Site: full width */
  .td[data-column="site"] {
    grid-column: 1 / -1;
  }

  /* Status row (tags column removed) */
  .td[data-column="status"] {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  /* Checked + Changed side by side */
  .td[data-column="lastChecked"],
  .td[data-column="lastChanged"] {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .td[data-column="lastChecked"]::before {
    content: "Last checked";
    font-size: 0.6rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-secondary);
  }

  .td[data-column="lastChanged"]::before {
    content: "Last changed";
    font-size: 0.6rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-secondary);
  }

  .td[data-column="lastChecked"] {
    grid-column: 1;
    grid-row: 3;
  }

  .td[data-column="lastChanged"] {
    grid-column: 2;
    grid-row: 3;
    justify-self: start;
    margin-left: 1.5rem;
  }

  /* Actions: full width, evenly spaced */
  .td[data-column="actions"] {
    grid-column: 1 / -1;
    grid-row: 4;
  }

  /* An actual grid, rather than a wrapping row that grows taller instead of
     shrinking its controls. Same intent - fingers, not cursors - but the count of
     buttons varies with plan and watch state, and under flex that produced rows of
     two, then four, then one, each a different width. Four equal columns give the
     same answer whatever is in them. */
  .watchActions {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    width: 100%;
    gap: 0.35rem;
  }

  .actionBtn {
    width: auto;
    min-width: 0;
    /* Room for an icon over a label without the label touching the border. */
    height: 52px;
    padding: 0 0.2rem;
    flex-direction: column;
    gap: 3px;
  }

  .actionIcon {
    width: 16px;
    height: 16px;
  }

  /* The tooltip becomes a label - there is no hover to trigger it, and leaving
     these as bare glyphs gave no way to find out what any of them did.

     `data-label`, not `title`. A tooltip is written to be read on demand and can
     afford a sentence; the same string used as a permanent label gives you
     "Auto-tune filters with AI (0/10 today)" under a 16px icon, which is how one
     button ended up three lines tall and wider than the four beside it. The two
     jobs need two strings. */
  .actionBtn::after {
    content: attr(data-label);
    position: static;
    transform: none;
    opacity: 1;
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
    font-size: 0.575rem;
    line-height: 1.1;
    color: var(--text-secondary);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* A disabled control still has to say what it is. Hiding the label left two
     unexplained glyphs on any watch that had not changed yet - which is every
     watch on its first day. */
  .actionBtnDisabled::after {
    display: block;
  }

  /* "Pro feature" works as a tooltip, where the icon is already under the cursor
     and the question is why it is greyed out. As the permanent label it is the one
     entry in the row that names a pricing tier instead of an action, and the only
     one that does not say what the button does. The dimming already says it is
     locked. */
  .screenshotLocked::after {
    content: attr(data-label) !important;
  }

  /* The remaining-uses badge is pinned to the button's bottom-right corner, which
     on desktop is empty space. Here that corner holds the label, so the number
     landed on top of the word. Moved up beside the icon. */
  /* One grid cell, like every other control. The button inside has to fill it, or
     this one sits narrower than its neighbours for no visible reason. */
  .recheckWrap {
    min-width: 0;
  }

  .recheckWrap .actionBtn {
    width: 100%;
  }

  .recheckCounter {
    top: 4px;
    right: 4px;
    bottom: auto;
    font-size: 9px;
  }

  .filterToolbar {
    flex-wrap: wrap;
    gap: 0.375rem;
    padding: 0.5rem;
  }

  .toolbarSearch {
    flex-basis: 100%;
  }

  .toolbarDivider {
    display: none;
  }

  .toolbarFilters {
    flex-wrap: wrap;
    width: 100%;
  }

  .tagDropdown,
  .tagSelectorDropdown,
  .tagFilterGrid {
    max-width: calc(100vw - 2rem);
  }
}

@media (max-width: 480px) {
  .addWatchRow {
    flex-direction: column;
  }

  .urlInputGroup {
    width: 100%;
  }

  .addWatchBtn {
    width: 100%;
    min-height: 44px;
  }

  .filterToolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbarFilters {
    justify-content: center;
  }

  .filterBtn {
    width: auto;
    height: 44px;
  }

  .emptyState {
    padding: 2.5rem 1.25rem;
  }

  .loading {
    padding: 2.5rem 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .loadingSkeleton {
    animation: none;
  }

  .spinner,
  .miniSpinner {
    animation: none;
  }

  .tableRow:hover {
    transform: none;
  }
}

.reliabilityHelpLink {
  font-size: 11px;
  line-height: 1.3;
  color: var(--text-accent);
  text-decoration: underline;
}

.reliabilityHelpLink:hover {
  opacity: 0.85;
}

/* Link out of the "Monitor Only" card into the visual picker. Typing a CSS selector
   by hand is the fallback, not the intended path — pointing at the element is. */
.pickOnPageLink {
  display: inline-block;
  margin-top: 0.6rem;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--card-border);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.8rem;
  text-decoration: none;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
}

.pickOnPageLink:hover {
  border-color: var(--text-accent);
  background: var(--btn-hover-bg);
}

.pickOnPageLink:focus-visible {
  outline: 2px solid var(--text-accent);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .pickOnPageLink {
    transition: none;
  }
}

.browserSection {
  margin-top: 1.5rem;
}

.browserCard {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-bg);
  font-family: var(--font-body);
}

.browserSelects {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.browserSelectGroup {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1 1 10rem;
  min-width: 0;
}

.browserSelectLabel {
  font-size: 0.8125rem;
  color: var(--text-secondary);
}

.browserResetBtn {
  background: none;
  border: none;
  padding: 0;
  margin-right: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  cursor: pointer;
  text-decoration: underline;
}

.browserResetBtn:hover {
  color: var(--text-primary);
}

.checkEstimate {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
}
